/**
 * Small cues for the voice orb: a buzz and, if the person wants it, a sound.
 *
 * Owns one concern: telling a person, without looking, that their tap landed,
 * the call connected, or it ended. Haptics are on wherever the device has them
 * (a phone; desktops ignore them) and are short. Sounds are off until the person
 * turns them on in the orb's dock, so nothing beeps unasked. Both are best
 * effort: a browser that refuses either changes nothing else about the call.
 */
import * as React from "react";

export type Cue = "tap" | "connected" | "ended";

const SOUND_KEY = "omni.orb.sound";
const BUZZ: Record<Cue, number | number[]> = { tap: 8, connected: [10, 40, 14], ended: [16, 30, 8] };
const NOTES: Record<Cue, number[]> = { tap: [], connected: [523, 784], ended: [784, 523] };

export function haptic(cue: Cue): void {
  try {
    navigator.vibrate?.(BUZZ[cue]);
  } catch {
    /* no vibration motor, or the browser blocks it without a gesture */
  }
}

export function tone(cue: Cue): void {
  const notes = NOTES[cue];
  if (notes.length === 0) return;
  try {
    const ctx = new AudioContext();
    const now = ctx.currentTime;
    notes.forEach((hz, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = hz;
      // Quieter than the wake chime: this plays on every call, not once per wake.
      gain.gain.setValueAtTime(0.0001, now + i * 0.1);
      gain.gain.exponentialRampToValueAtTime(0.07, now + i * 0.1 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.1 + 0.09);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.1);
      osc.stop(now + i * 0.1 + 0.1);
    });
    window.setTimeout(() => void ctx.close(), 400);
  } catch {
    /* no audio output */
  }
}

function remembered(): boolean {
  try {
    return window.localStorage.getItem(SOUND_KEY) === "1";
  } catch {
    return false;
  }
}

/** The orb's cues: a tap buzz, and a connected / ended cue as the call changes. */
export function useOrbFeedback(connecting: boolean, voiceActive: boolean) {
  const [soundOn, setSoundState] = React.useState(remembered);
  const setSoundOn = React.useCallback((on: boolean) => {
    setSoundState(on);
    try {
      window.localStorage.setItem(SOUND_KEY, on ? "1" : "0");
    } catch {
      /* the choice lasts until the tab closes */
    }
  }, []);

  const soundRef = React.useRef(soundOn);
  soundRef.current = soundOn;
  const wasConnected = React.useRef(false);
  React.useEffect(() => {
    // Only a call that really connected has an end worth marking: a start that failed already shows its error.
    if (voiceActive && !wasConnected.current) {
      haptic("connected");
      if (soundRef.current) tone("connected");
    } else if (!voiceActive && !connecting && wasConnected.current) {
      haptic("ended");
      if (soundRef.current) tone("ended");
    }
    wasConnected.current = voiceActive;
  }, [voiceActive, connecting]);

  const tap = React.useCallback(() => {
    haptic("tap");
  }, []);
  return { soundOn, setSoundOn, tap };
}
